"use client"

import React, { useState, useEffect, useCallback } from "react"
import { Search, Phone, MapPin, ShoppingBag, Star, Clock, UserPlus, Eye, ChevronLeft, ChevronRight, Users, UserCheck, PhoneCall, Smartphone } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { customerAPI } from "../services/api"
import PageHeader from "../components/ui/dashboard/PageHeader"
import CustomerDetailModal from "../components/CustomerDetailModal"

interface Customer {
    _id: string
    phoneNumber: string
    username: string
    fullName?: string
    email?: string
    profileImage?: string
    isActive: boolean
    isVerified: boolean
    registrationSource: "app" | "phone_order" | "web"
    isPartialRegistration: boolean
    savedAddresses: Array<{
        _id: string
        label: string
        street?: string
        city?: string
        fullAddress?: string
        isDefault: boolean
    }>
    createdAt: string
    orderStats: {
        totalOrders: number
        totalSpent: number
        lastOrderDate: string | null
    }
}

interface CustomerStats {
    totalCustomers: number
    activeCustomers: number
    phoneOrderCustomers: number
    appCustomers: number
    newThisMonth: number
}

const CustomersPage: React.FC = () => {
    const { t, isRTL } = useLanguage()
    const { toast } = useToast()

    const [customers, setCustomers] = useState<Customer[]>([])
    const [loading, setLoading] = useState(true)
    const [stats, setStats] = useState<CustomerStats | null>(null)
    const [searchQuery, setSearchQuery] = useState("")
    const [sourceFilter, setSourceFilter] = useState<string>("all")
    const [statusFilter, setStatusFilter] = useState<string>("")
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalCustomers, setTotalCustomers] = useState(0)
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [phoneSearch, setPhoneSearch] = useState("")
    const [searchingPhone, setSearchingPhone] = useState(false)

    const fetchCustomers = useCallback(async () => {
        try {
            setLoading(true)
            const params: any = {
                page: currentPage,
                limit: 20,
            }
            if (searchQuery) params.search = searchQuery
            if (sourceFilter !== "all") params.registrationSource = sourceFilter
            if (statusFilter) params.isActive = statusFilter

            const response = await customerAPI.getCustomers(params)
            if (response.data.success) {
                setCustomers(response.data.data.customers)
                setTotalPages(response.data.data.pagination.totalPages)
                setTotalCustomers(response.data.data.pagination.totalCustomers)
            }
        } catch (error) {
            console.error("Failed to fetch customers:", error)
            toast({
                title: t("error"),
                description: t("failedToLoadCustomers"),
                variant: "destructive",
            })
        } finally {
            setLoading(false)
        }
    }, [currentPage, searchQuery, sourceFilter, statusFilter, t, toast])

    const fetchStats = useCallback(async () => {
        try {
            const response = await customerAPI.getCustomerStats()
            if (response.data.success) {
                setStats(response.data.data)
            }
        } catch (error) {
            console.error("Failed to fetch customer stats:", error)
        }
    }, [])

    useEffect(() => {
        fetchCustomers()
        fetchStats()
    }, [fetchCustomers, fetchStats])

    const handlePhoneSearch = async () => {
        if (!phoneSearch.trim()) return

        try {
            setSearchingPhone(true)
            const response = await customerAPI.searchByPhone(phoneSearch)
            if (response.data.success && response.data.data.found) {
                setSelectedCustomer(response.data.data.customer)
                setShowDetailModal(true)
            } else {
                toast({
                    title: t("customerNotFound"),
                    description: t("noCustomerWithPhone"),
                })
            }
        } catch (error) {
            console.error("Phone search error:", error)
            toast({
                title: t("error"),
                description: t("searchFailed"),
                variant: "destructive",
            })
        } finally {
            setSearchingPhone(false)
        }
    }

    const handleViewCustomer = (customer: Customer) => {
        setSelectedCustomer(customer)
        setShowDetailModal(true)
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "SDG",
        }).format(amount)
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return t("never")
        return new Date(dateString).toLocaleDateString()
    }

    const getSourceBadge = (source: string) => {
        switch (source) {
            case "app":
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        <Smartphone className="w-3 h-3" />
                        {t("mobileApp")}
                    </span>
                )
            case "phone_order":
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                        <PhoneCall className="w-3 h-3" />
                        {t("phoneOrder")}
                    </span>
                )
            default:
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                        {t("web")}
                    </span>
                )
        }
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={t("customers")}
                subtitle={t("manageCustomers")}
            />
            <div className="space-y-6">
                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{t("totalCustomers")}</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCustomers}</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                    <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{t("activeCustomers")}</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeCustomers}</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                    <PhoneCall className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{t("phoneOrderCustomers")}</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.phoneOrderCustomers}</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                    <Smartphone className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{t("appCustomers")}</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.appCustomers}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Phone Search */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t("quickPhoneSearch")}</h3>
                    <div className="flex gap-2">
                        <div className="relative flex-1 max-w-md">
                            <Phone className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? "right-3" : "left-3"}`} />
                            <input
                                type="tel"
                                value={phoneSearch}
                                onChange={(e) => setPhoneSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handlePhoneSearch()}
                                placeholder={t("enterPhoneNumber")}
                                className={`w-full ${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                            />
                        </div>
                        <button
                            onClick={handlePhoneSearch}
                            disabled={searchingPhone || !phoneSearch.trim()}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {searchingPhone ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Search className="w-4 h-4" />
                            )}
                            {t("search")}
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex flex-wrap gap-4">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? "right-3" : "left-3"}`} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value)
                                    setCurrentPage(1)
                                }}
                                placeholder={t("searchCustomers")}
                                className={`w-full ${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                            />
                        </div>
                        <select
                            value={sourceFilter}
                            onChange={(e) => {
                                setSourceFilter(e.target.value)
                                setCurrentPage(1)
                            }}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500"
                        >
                            <option value="all">{t("allSources")}</option>
                            <option value="app">{t("mobileApp")}</option>
                            <option value="phone_order">{t("phoneOrder")}</option>
                        </select>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value)
                                setCurrentPage(1)
                            }}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500"
                        >
                            <option value="">{t("allStatus")}</option>
                            <option value="true">{t("active")}</option>
                            <option value="false">{t("inactive")}</option>
                        </select>
                    </div>
                </div>

                {/* Customers Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : customers.length === 0 ? (
                        <div className="text-center py-12">
                            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 dark:text-gray-400">{t("noCustomersFound")}</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                        <tr>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : "text-left"}`}>
                                                {t("customer")}
                                            </th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : "text-left"}`}>
                                                {t("phone")}
                                            </th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : "text-left"}`}>
                                                {t("source")}
                                            </th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : "text-left"}`}>
                                                {t("orders")}
                                            </th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : "text-left"}`}>
                                                {t("totalSpent")}
                                            </th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : "text-left"}`}>
                                                {t("lastOrder")}
                                            </th>
                                            <th className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${isRTL ? "text-right" : "text-left"}`}>
                                                {t("actions")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {customers.map((customer) => (
                                            <tr key={customer._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                                                            {customer.profileImage ? (
                                                                <img src={customer.profileImage} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                                                    {(customer.fullName || customer.username || "?").charAt(0).toUpperCase()}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">
                                                                {customer.fullName || customer.username}
                                                            </p>
                                                            {customer.email && (
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">{customer.email}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-gray-900 dark:text-white">{customer.phoneNumber}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {getSourceBadge(customer.registrationSource)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1">
                                                        <ShoppingBag className="w-4 h-4 text-gray-400" />
                                                        <span className="text-gray-900 dark:text-white">{customer.orderStats.totalOrders}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="font-medium text-gray-900 dark:text-white">
                                                        {formatCurrency(customer.orderStats.totalSpent)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                                                        <Clock className="w-4 h-4" />
                                                        <span>{formatDate(customer.orderStats.lastOrderDate)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <button
                                                        onClick={() => handleViewCustomer(customer)}
                                                        className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("showingOf", { count: customers.length, total: totalCustomers })}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                                    </button>
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        {currentPage} / {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Customer Detail Modal */}
            {showDetailModal && selectedCustomer && (
                <CustomerDetailModal
                    customer={selectedCustomer}
                    onClose={() => {
                        setShowDetailModal(false)
                        setSelectedCustomer(null)
                    }}
                    onUpdate={() => {
                        fetchCustomers()
                        fetchStats()
                    }}
                />
            )}
        </div>
    )
}

export default CustomersPage
